import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Search } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { CarduriSelector, type CardSelector } from '../../components/ui/CarduriSelector'
import { StareLista } from '../../components/ui/StareLista'
import { Button } from '../../components/ui/Button'
import { FaraComanda } from './FaraComanda'
import { etichetaSellIn } from '@b4l/shared'
import { dataScurta, eroare, esteRestant, lei, type RandClient } from './comun'

/*
  Vânzări → Clienții mei (task „Structura Clienti", Victor 19.10.2026; Adrian 20.10; macheta
  aprobata 20.10). Lista clientilor, cu banii si semnalele care cer atentie scoase in fata;
  clic pe un client deschide fisa lui.

  Cifrele sunt ale Tabloului de bord vanzari — aceeasi socoteala, deci aceleasi sume pe ambele
  ecrane. Restanta (scadent peste termen) iese ingrosata si rosie, cu zilele (Adrian).
*/

interface Raspuns {
  scope: {
    agent: string | null
    canFilter: boolean
    faraConturiNationale: boolean
    unmapped: boolean
  }
  clienti: RandClient[]
  carduri: {
    toti: number
    sellIn: number
    restante: { count: number; suma: number }
    scadente7: { count: number; suma: number }
    faraComanda: number
    selloutLipsa: number
  }
}

type Card = 'toti' | 'restante' | 'scadente7' | 'faraComanda' | 'selloutLipsa'
type Sortare = 'restante' | 'nume' | 'sellIn' | 'comanda' | 'deIncasat'

const FILTRU: Record<Card, (c: RandClient) => boolean> = {
  toti: () => true,
  restante: esteRestant,
  scadente7: (c) => c.scadente7.count > 0,
  faraComanda: (c) => c.pauza != null,
  selloutLipsa: (c) => (c.sellout?.lipsa ?? 0) > 0,
}

const SORT: Record<Sortare, (a: RandClient, b: RandClient) => number> = {
  restante: (a, b) =>
    (b.restant ?? 0) - (a.restant ?? 0) ||
    (b.zileIntarziere ?? 0) - (a.zileIntarziere ?? 0) ||
    a.nume.localeCompare(b.nume, 'ro'),
  nume: (a, b) => a.nume.localeCompare(b.nume, 'ro'),
  sellIn: (a, b) => b.sellIn - a.sellIn,
  comanda: (a, b) => (b.ultimaComanda ?? '').localeCompare(a.ultimaComanda ?? ''),
  deIncasat: (a, b) => b.deIncasat - a.deIncasat,
}

export function ClientiiMeiPage() {
  const qc = useQueryClient()
  const eAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const [agent, setAgent] = useState('')
  // Din notificarea de dimineata („…n-au mai comandat de mult") se ajunge direct pe card.
  const [cauta] = useSearchParams()
  const [card, setCard] = useState<Card>(
    cauta.get('card') === 'faraComanda' ? 'faraComanda' : 'toti',
  )
  // Si cand esti deja pe pagina si apesi notificarea: linkul se schimba, pagina ramane.
  useEffect(() => {
    if (cauta.get('card') === 'faraComanda') setCard('faraComanda')
  }, [cauta])
  const [q, setQ] = useState('')
  const [sortare, setSortare] = useState<Sortare>('restante')
  const [grupat, setGrupat] = useState(true)

  const r = useQuery<Raspuns>({
    queryKey: ['clienti', 'lista', agent],
    queryFn: async () =>
      (await api.get<Raspuns>('/clienti', { params: agent ? { agent } : {}, timeout: 120_000 }))
        .data,
    staleTime: 60_000,
  })
  const toti = useMemo(() => r.data?.clienti ?? [], [r.data])
  const poateFiltra = r.data?.scope.canFilter ?? false

  // Agentii din lista (la admin / manager): doar cei care chiar au clienti.
  const agenti = useMemo(
    () =>
      [...new Set(toti.map((c) => c.agent).filter((a): a is string => !!a))].sort((a, b) =>
        a.localeCompare(b, 'ro'),
      ),
    [toti],
  )

  const vizibili = useMemo(() => {
    const t = q.trim().toLowerCase()
    return toti
      .filter(FILTRU[card])
      .filter(
        (c) => !t || c.nume.toLowerCase().includes(t) || (c.oras ?? '').toLowerCase().includes(t),
      )
      .sort(SORT[sortare])
  }, [toti, card, q, sortare])

  const grupuri = useMemo(() => {
    if (!grupat || !poateFiltra || agent)
      return [{ agent: null as string | null, clienti: vizibili }]
    const m = new Map<string, RandClient[]>()
    for (const c of vizibili) {
      const k = c.agent ?? 'Fără agent'
      m.set(k, [...(m.get(k) ?? []), c])
    }
    return [...m]
      .sort((a, b) => a[0].localeCompare(b[0], 'ro'))
      .map(([agent, clienti]) => ({ agent, clienti }))
  }, [vizibili, grupat, poateFiltra, agent])

  const k = r.data?.carduri
  const carduri: CardSelector<Card>[] = [
    {
      cheie: 'toti',
      eticheta: 'Toți clienții',
      valoare: k?.toti ?? '—',
      detaliu: k ? `${lei(k.sellIn)} sell-in în ${new Date().getFullYear()}` : null,
      ton: 'albastru',
    },
    {
      cheie: 'restante',
      eticheta: 'Cu restanțe',
      valoare: k?.restante.count ?? '—',
      detaliu: k ? `${lei(k.restante.suma)} peste termen` : null,
      ton: k?.restante.count ? 'rosu' : 'gri',
    },
    {
      cheie: 'scadente7',
      eticheta: 'Scadente în 7 zile',
      valoare: k?.scadente7.count ?? '—',
      detaliu: k ? `${lei(k.scadente7.suma)} de încasat` : null,
      ton: 'gri',
    },
    {
      cheie: 'faraComanda',
      eticheta: 'Fără comandă',
      valoare: k?.faraComanda ?? '—',
      detaliu: '60+ zile sau peste ritmul lor · de sunat',
      ton: k?.faraComanda ? 'galben' : 'gri',
    },
    {
      cheie: 'selloutLipsa',
      eticheta: 'Sell-out lipsă',
      valoare: k?.selloutLipsa ?? '—',
      detaliu: 'raportul n-a venit',
      ton: k?.selloutLipsa ? 'galben' : 'gri',
    },
  ]

  const sincronizeaza = useMutation({
    mutationFn: async () =>
      (await api.post('/agents/sync-all-clients', undefined, { timeout: 300_000 })).data as {
        clients: { totalImported: number; created: number }
      },
    onSuccess: (x) => {
      toast.success(`Sincronizat: ${x.clients.totalImported} clienți (${x.clients.created} noi).`)
      void qc.invalidateQueries({ queryKey: ['clienti'] })
    },
    onError: (e) => toast.error(eroare(e, 'Sincronizarea a eșuat.')),
  })

  const descriere = r.data
    ? `${r.data.scope.agent ?? (agent || 'Toți agenții')} · ${k?.toti ?? 0} clienți · aceleași cifre ca Tabloul de bord. Clic pe un client: fișa lui.`
    : 'Clienții tăi, cu banii și ce cere atenție.'

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="Clienții mei"
        descriere={descriere}
        actiuni={
          <div className="flex flex-wrap items-center gap-2">
            {poateFiltra && (
              <label className="flex items-center gap-1.5 text-corp text-b4l-text-muted">
                Agent
                <select
                  value={agent}
                  onChange={(e) => setAgent(e.target.value)}
                  className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-b4l-text"
                >
                  <option value="">Toți agenții</option>
                  {agenti.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {poateFiltra && !agent && (
              <label className="flex items-center gap-2 rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp text-b4l-text">
                <input
                  type="checkbox"
                  checked={grupat}
                  onChange={(e) => setGrupat(e.target.checked)}
                />
                Grupează pe agent
              </label>
            )}
            {eAdmin && (
              <Button
                variant="secondary"
                onClick={() => sincronizeaza.mutate()}
                loading={sincronizeaza.isPending}
              >
                Sincronizează clienții din EA
              </Button>
            )}
          </div>
        }
      />

      {r.data?.scope.unmapped ? (
        <p className="rounded-xl border border-b4l-border bg-b4l-surface p-6 text-center text-corp text-b4l-text-muted">
          Contul tău nu e legat de un agent din ExpertAccounts, deci nu ai clienți în portofoliu.
          Cere unui administrator să completeze numele agentului pe utilizator.
        </p>
      ) : (
        <>
          <CarduriSelector
            carduri={carduri}
            ales={card}
            onAlege={setCard}
            eticheta="Ce cere atenție"
          />

          {card === 'faraComanda' ? (
            <StareLista interogare={r} gol={false} mesajGol={null}>
              <FaraComanda clienti={toti} />
            </StareLista>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex min-w-[240px] flex-1 items-center gap-2 rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-b4l-text-dim">
                  <Search size={15} aria-hidden="true" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Caută client sau oraș"
                    aria-label="Caută client"
                    className="min-w-0 flex-1 bg-transparent text-corp text-b4l-text outline-none"
                  />
                </label>
                <label className="flex items-center gap-1.5 text-corp text-b4l-text-muted">
                  Sortează
                  <select
                    value={sortare}
                    onChange={(e) => setSortare(e.target.value as Sortare)}
                    className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-b4l-text"
                  >
                    <option value="restante">Restanțe întâi</option>
                    <option value="deIncasat">De încasat</option>
                    <option value="sellIn">{etichetaSellIn()}</option>
                    <option value="comanda">Ultima comandă</option>
                    <option value="nume">Nume</option>
                  </select>
                </label>
              </div>

              <StareLista
                interogare={r}
                gol={vizibili.length === 0}
                mesajGol={
                  card === 'restante'
                    ? 'Niciun client cu restanțe. Toate facturile sunt în termen.'
                    : q
                      ? `Niciun client cu „${q}”.`
                      : 'Niciun client aici.'
                }
              >
                <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
                  <table className="w-full min-w-[900px] border-collapse text-corp">
                    <thead>
                      <tr className="text-left text-mic text-b4l-text-muted">
                        <th
                          scope="col"
                          className="border-b border-b4l-border px-3.5 py-2.5 font-medium"
                        >
                          Client
                        </th>
                        <th
                          scope="col"
                          className="border-b border-b4l-border px-2 py-2.5 font-medium"
                        >
                          Ultima comandă
                        </th>
                        <th
                          scope="col"
                          className="border-b border-b4l-border px-2 py-2.5 text-right font-medium"
                        >
                          {etichetaSellIn()}
                        </th>
                        <th
                          scope="col"
                          className="border-b border-b4l-border px-2 py-2.5 text-right font-medium"
                        >
                          De încasat
                        </th>
                        <th
                          scope="col"
                          className="border-b border-b4l-border px-2 py-2.5 text-right font-medium"
                        >
                          Restant
                        </th>
                        <th
                          scope="col"
                          className="border-b border-b4l-border px-2 py-2.5 text-right font-medium"
                        >
                          Sell-out
                        </th>
                        <th
                          scope="col"
                          className="border-b border-b4l-border px-3.5 py-2.5 text-right font-medium"
                        >
                          Target
                        </th>
                      </tr>
                    </thead>
                    {grupuri.map((g) => (
                      <tbody key={g.agent ?? '-'}>
                        {g.agent && (
                          <tr className="bg-b4l-bg-soft">
                            <th
                              colSpan={7}
                              scope="rowgroup"
                              className="border-b border-b4l-border px-3.5 py-2 text-left text-mic"
                            >
                              <span className="font-semibold text-b4l-text">{g.agent}</span>
                              <span className="ml-2 font-normal text-b4l-text-muted">
                                {g.clienti.length} clienți ·{' '}
                                {lei(g.clienti.reduce((s, c) => s + c.sellIn, 0))} sell-in ·{' '}
                                {lei(
                                  g.clienti
                                    .filter(esteRestant)
                                    .reduce((s, c) => s + (c.restant ?? 0), 0),
                                )}{' '}
                                restant
                              </span>
                            </th>
                          </tr>
                        )}
                        {g.clienti.map((c) => (
                          <RandLista key={c.id} c={c} />
                        ))}
                      </tbody>
                    ))}
                  </table>
                </div>
              </StareLista>
            </>
          )}
        </>
      )}
    </div>
  )
}

function RandLista({ c }: { c: RandClient }) {
  const veche = c.pauza != null
  const restant = esteRestant(c)
  return (
    <tr className="hover:bg-b4l-bg-soft">
      <td className="border-b border-b4l-border px-3.5 py-2.5">
        <Link to={`/clienti/${c.id}`} className="font-semibold text-b4l-text hover:underline">
          {c.nume}
        </Link>
        <span className="block text-mic text-b4l-text-muted">
          {[c.oras, c.puncte ? `${c.puncte} ${c.puncte === 1 ? 'punct' : 'puncte'}` : null, c.grup]
            .filter(Boolean)
            .join(' · ') || '—'}
        </span>
      </td>
      <td
        className={`border-b border-b4l-border px-2 py-2.5 ${veche ? 'font-semibold text-b4l-amber-dark' : 'text-b4l-text'}`}
      >
        {c.ultimaComanda
          ? veche
            ? `acum ${c.pauza!.zile} zile`
            : dataScurta(c.ultimaComanda)
          : 'niciodată'}
      </td>
      <td className="border-b border-b4l-border px-2 py-2.5 text-right tabular-nums">
        {lei(c.sellIn)}
      </td>
      <td className="border-b border-b4l-border px-2 py-2.5 text-right tabular-nums">
        {c.deIncasat < -0.5 ? (
          // Sold creditor: a platit in plus, nu e de cerut (aceeasi regula ca pe fisa).
          <span className="text-b4l-green-dark" title="Plătit în plus (sold creditor)">
            {lei(-c.deIncasat)} în plus
          </span>
        ) : (
          lei(c.deIncasat)
        )}
      </td>
      <td
        className={`border-b border-b4l-border px-2 py-2.5 text-right tabular-nums ${restant ? 'font-bold text-b4l-red-dark' : 'text-b4l-text-dim'}`}
      >
        {restant ? (
          <>
            {lei(c.restant)}
            {c.zileIntarziere ? (
              <span className="block text-mic font-semibold">
                {c.zileIntarziere} zile peste termen
              </span>
            ) : null}
          </>
        ) : (
          '—'
        )}
      </td>
      <td
        className={`border-b border-b4l-border px-2 py-2.5 text-right ${(c.sellout?.lipsa ?? 0) > 0 ? 'font-semibold text-b4l-amber-dark' : 'text-b4l-text-muted'}`}
      >
        {!c.sellout
          ? '—'
          : c.sellout.lipsa > 0
            ? `lipsă ${c.sellout.lipsa} ${c.sellout.lipsa === 1 ? 'raport' : 'rapoarte'}`
            : `${c.sellout.pct}% vândut`}
      </td>
      <td className="border-b border-b4l-border px-3.5 py-2.5 text-right text-b4l-text-muted">
        {c.pctTarget != null ? `${Math.round(c.pctTarget)}%` : '—'}
      </td>
    </tr>
  )
}
